import React from "react";
import { Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { Box, Skeleton } from "@mui/material";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
import { OcChartLegend, OcCurrency } from "oc/components";

ChartJS.register(ArcElement, Tooltip, Legend);

const CHART_SIZE = 200;

interface MyCafeteriaSummaryChartProps {
  allAmount: number;
  allBenefits: number;
  allCosts: number;
  availableAmount: number;
  loading: boolean;
  error?: { message: string };
  isMobile: boolean;
}

export const MyCafeteriaSummaryChart: React.FC<
  MyCafeteriaSummaryChartProps
> = ({
  allAmount,
  allBenefits,
  allCosts,
  availableAmount,
  loading,
  error,
  isMobile,
}) => {
    const { t } = useTranslation();

    const data = {
      labels: ["Benefits", "Costs", "Remain"],
      datasets: [
        {
          data: [allBenefits, allCosts, availableAmount],
          backgroundColor: ["#cc0033", "#F3AA18", "#2FA84F"],
          borderWidth: 0,
        },
      ],
    };

    const loadingData = {
      labels: ["Loading"],
      datasets: [
        {
          data: [100],
          backgroundColor: ["#E3E5E5"],
          borderWidth: 0,
        },
      ],
    };

    let summary = allAmount;

    if (error)
      return (
        <Box
          height="232px"
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          style={{ border: "1px solid #cc0033", color: "#cc0033" }}
        >
          <Box>{error.message}</Box>
        </Box>
      );

    return (
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="center"
        paddingTop="16px"
      >
        <Box
          width={isMobile ? "100%" : "440px"}
          height={isMobile ? "auto" : "216px"}
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="center"
          alignItems={isMobile ? "center" : "unset"}
        >
          <Box position="relative" width={isMobile ? "200px" : "auto"}>
            <Doughnut
              options={{
                cutout: "83%",
                responsive: true,
                plugins: {
                  tooltip: { enabled: true },
                  legend: {
                    display: false,
                  },
                },
                animation: {
                  duration: 1000,
                },
              }}
              width={CHART_SIZE}
              height={CHART_SIZE}
              data={loading ? loadingData : data}
            />
            <Box
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
              }}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <Box display="flex" flexDirection="column" alignItems="center">
                <Box
                  style={{
                    marginTop: "4px",
                    fontFamily: "Mulish",
                    fontSize: summary?.toString().length >= 6 ? "22px" : "26px",
                    lineHeight: "32px",
                    letterSpacing: "0.19px",
                    color: "#232425",
                  }}
                >
                  {loading ? (
                    <Skeleton width={120} height={32} />
                  ) : (
                    OcCurrency({ value: summary })
                  )}
                </Box>
                <Box
                  style={{
                    fontFamily: "Mulish",
                    fontSize: "12px",
                    lineHeight: "16px",
                    letterSpacing: "0.4px",
                    color: "#919699",
                    marginTop: "16px",
                  }}
                >
                  {t("MY_CAFETERIA_BUDGET")}
                </Box>
              </Box>
            </Box>
          </Box>
          {isMobile && <Box height="16px" />}
          <Box width="70px" />
          <Box minWidth="200px" width={isMobile ? "100%" : "auto"}>
            <OcChartLegend
              label={t("MY_CAFETERIA_ALL_AMOUNT")}
              value={OcCurrency({ value: allBenefits })}
              loading={loading}
              color="#cc0033"
              isMobile={isMobile}
              showDivider={true}
            />
            <OcChartLegend
              label={t("MY_CAFETERIA_ALL_COSTS")}
              value={OcCurrency({ value: allCosts })}
              loading={loading}
              color="#F3AA18"
              isMobile={isMobile}
              showDivider={true}
            />
            <OcChartLegend
              label={t("MY_CAFETERIA_REMAINS")}
              showDivider={false}
              value={OcCurrency({ value: availableAmount })}
              loading={loading}
              color="#2FA84F"
              isMobile={isMobile}
            />
          </Box>
        </Box>
      </Box>
    );
  };

export default MyCafeteriaSummaryChart;
